import { hasSelectedAssets } from './../filters.js';

/**
 * The maintenance report records received from the API look as follows:
 *
 * [
 *      {
 *          "facility_id": 1,
 *          "asset_id": 23,
 *          "asset_name": "Filler 1",
 *          "cause_id": 1,
 *          "cause_name": "Cause 1",
 *          "downtime_duration_min": 5.73,
 *          "running_duration_min": 10.32,
 *          "equipment_failure_duration_min": 1.23,
 *          "number_downtimes": 12,
 *          "number_equipment_failures": 3,
 *          "mttr_min": 24.6,
 *          "mtbf_min": 205.6
 *      },
 *      {
 *          "facility_id": 1,
 *          "asset_id": 24,
 *          "asset_name": "Filler 2",
 *          "cause_id": 2,
 *          "cause_name": "Cause 2",
 *          "downtime_duration_min": 6.88,
 *          "running_duration_min": 12.6,
 *          "equipment_failure_duration_min": 2.44,
 *          "number_downtimes": 9,
 *          "number_equipment_failures": 2,
 *          "mttr_min": 12.5,
 *          "mtbf_min": 187.3
 *      }
 * ]
 *
 *
 * The maintenance report that is generated by the buildTable() function has the following form:
 *
 * {
 *      rows: [
 *          {
 *              asset_id: (number),
 *              asset_name: (string),
 *              causes: [
 *                  {
 *                      cause_id: (number),
 *                      cause_name: (string),
 *                      duration_min: (number)
 *                      running_duration_min: (number),
 *                      downtime_duration_min: (number),
 *                      equipment_failure_duration_min: (number),
 *                      number_downtimes: (number),
 *                      number_equipment_failures: (number),
 *                      mttr_min: (number),
 *                      mtbf_min: (number)
 *                  },
 *                  ...
 *              ],
 *              running_duration_min: (number),
 *              downtime_duration_min: (number),
 *              equipment_failure_duration_min: (number),
 *              number_downtimes: (number),
 *              number_equipment_failures: (number),
 *              mttr_min: (number),
 *              mtbf_min: (number)
 *          },
 *          ...
 *      ],
 *      totals: {
 *          running_duration_min: (number),
 *          downtime_duration_min: (number),
 *          equipment_failure_duration_min: (number),
 *          number_downtimes: (number),
 *          number_equipment_failures: (number),
 *          mttr_min: (number),
 *          mtbf_min: (number)
 *      }
 * }
 *
 */

/**
 * Build the maintenance report table from records and filters.
 * @param {Array} records - Array of maintenance records to process
 * @param {Object} filters - Filters to apply to the records
 * @param {boolean} filters.minor - Include minor incidents
 * @param {boolean} filters.major - Include major incidents
 * @param {Set} filters.assetIds - Set of asset IDs to include
 * @param {Set} filters.shiftIds - Set of shift IDs to include
 * @returns {Object} Table object with rows and totals
 */
export function buildTable(records, filters) {
    const table = {
        rows: [],
        totals: getDefaultRowValues()
    };

    const assetMap = new Map();
    const processedAssetIds = new Set();

    // Process records in a single pass
    for (const record of records) {
        if (!isIncluded(record, filters)) continue;

        const assetId = record.asset_id;

        // Extract values with defaults
        const getValue = key => record[key] || 0;

        // Create cause object
        const causeObj = {
            // Use 'non_equipment_failures' for non-equipment failures as the cause id
            cause_id: !record.equipment_failure
                ? 'non_equipment_failures'
                : record.cause_id,
            cause_name: !record.equipment_failure
                ? 'Non-equipment failures'
                : record.cause_name,
            running_duration_min: getValue('running_duration_min'),
            downtime_duration_min: getValue('downtime_duration_min'),
            equipment_failure_duration_min: getValue(
                'equipment_failure_duration_min'
            ),
            number_downtimes: getValue('number_downtimes'),
            number_equipment_failures: getValue('number_equipment_failures'),
            equipment_failure: record.equipment_failure,
            mttr_min: getValue('mttr_min'),
            mtbf_min: getValue('mtbf_min')
        };

        // Update global totals
        if (!processedAssetIds.has(assetId)) {
            table.totals.running_duration_min += getValue(
                'running_duration_min'
            );
            processedAssetIds.add(assetId);
        }
        table.totals.downtime_duration_min += getValue('downtime_duration_min');
        table.totals.equipment_failure_duration_min += getValue(
            'equipment_failure_duration_min'
        );
        table.totals.number_downtimes += getValue('number_downtimes');
        table.totals.number_equipment_failures += getValue(
            'number_equipment_failures'
        );

        // Update or create asset entry
        if (!assetMap.has(assetId)) {
            // Create new asset with this cause
            assetMap.set(assetId, {
                asset_id: assetId,
                asset_name: record.asset_name,
                causes: [causeObj],
                running_duration_min: getValue('running_duration_min'),
                downtime_duration_min: getValue('downtime_duration_min'),
                equipment_failure_duration_min: getValue(
                    'equipment_failure_duration_min'
                ),
                number_downtimes: getValue('number_downtimes'),
                number_equipment_failures: getValue(
                    'number_equipment_failures'
                ),
                mttr_min: getValue('mttr_min'),
                mtbf_min: getValue('mtbf_min')
            });
        } else {
            const asset = assetMap.get(assetId);

            // Handle equipment failures and non-equipment failures differently
            if (record.equipment_failure === true) {
                // For equipment failures, find or add the exact cause
                const existingCause = asset.causes.find(
                    cause =>
                        cause.cause_id === record.cause_id &&
                        cause.equipment_failure === true
                );

                if (existingCause) {
                    // Update existing equipment failure cause
                    existingCause.downtime_duration_min += getValue(
                        'downtime_duration_min'
                    );
                    existingCause.equipment_failure_duration_min += getValue(
                        'equipment_failure_duration_min'
                    );
                    existingCause.number_downtimes +=
                        getValue('number_downtimes');
                    existingCause.number_equipment_failures += getValue(
                        'number_equipment_failures'
                    );

                    // Recalculate metrics
                    if (existingCause.number_equipment_failures > 0) {
                        existingCause.mttr_min =
                            existingCause.equipment_failure_duration_min /
                            existingCause.number_equipment_failures;
                        existingCause.mtbf_min =
                            existingCause.running_duration_min /
                            existingCause.number_equipment_failures;
                    }
                } else {
                    // Add new equipment failure cause
                    asset.causes.push(causeObj);
                }
            } else {
                // For non-equipment failures, find or create the special aggregated group
                const nonEquipmentGroup = asset.causes.find(
                    cause => cause.cause_id === 'non_equipment_failures'
                );

                if (nonEquipmentGroup) {
                    // Update existing non-equipment failures group - add the current record's values
                    nonEquipmentGroup.downtime_duration_min += getValue(
                        'downtime_duration_min'
                    );
                    nonEquipmentGroup.number_downtimes +=
                        getValue('number_downtimes');
                } else {
                    // Create a new non-equipment failures group
                    asset.causes.push({
                        cause_id: 'non_equipment_failures',
                        cause_name: 'Non-equipment failures',
                        running_duration_min: getValue('running_duration_min'),
                        downtime_duration_min: getValue(
                            'downtime_duration_min'
                        ),
                        equipment_failure_duration_min: 0, // Always 0 for non-equipment failures
                        number_downtimes: getValue('number_downtimes'),
                        number_equipment_failures: 0, // Always 0 for non-equipment failures
                        equipment_failure: false,
                        mttr_min: 0,
                        mtbf_min: 0
                    });
                }
            }

            // Update asset totals
            asset.downtime_duration_min += getValue('downtime_duration_min');
            asset.equipment_failure_duration_min += getValue(
                'equipment_failure_duration_min'
            );
            asset.number_downtimes += getValue('number_downtimes');
            asset.number_equipment_failures += getValue(
                'number_equipment_failures'
            );
        }
    }

    // Calculate final metrics for overall totals and assets
    const calculateMetrics = item => {
        if (item.number_equipment_failures > 0) {
            item.mttr_min =
                item.equipment_failure_duration_min /
                item.number_equipment_failures;
            item.mtbf_min =
                item.running_duration_min / item.number_equipment_failures;
        } else {
            item.mttr_min = 0;
            item.mtbf_min = 0;
        }
    };

    calculateMetrics(table.totals);
    assetMap.forEach(asset => calculateMetrics(asset));

    // Convert the Map to array of rows
    table.rows = Array.from(assetMap.values());

    return table;
}

/**
 * Determine whether a record should be included in the report.
 * @return {Boolean}
 */
export function isIncluded(record, filters) {
    return hasSelectedAssets(record, filters);
}

/**
 * Get the default row values.
 * @return {Object}
 */
function getDefaultRowValues() {
    return {
        running_duration_min: 0,
        downtime_duration_min: 0,
        equipment_failure_duration_min: 0,
        number_downtimes: 0,
        number_equipment_failures: 0,
        mttr_min: 0,
        mtbf_min: 0
    };
}
